// Phase MHM-2 · Dispatch Center — batch parcels into manifests.
import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Truck, Plus, X, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import TrackingIdLink from "../../components/hub/TrackingIdLink";
import SlaAlertBadge from "../../components/hub/SlaAlertBadge";
import RoleGate from "../../components/RoleGate";

function CreateManifestModal({ open, onClose, onCreated, preselected, couriers }) {
  const [plate, setPlate] = useState("");
  const [vtype, setVtype] = useState("van");
  const [driver, setDriver] = useState("");
  const [phone, setPhone] = useState("");
  const [courier, setCourier] = useState("auto");
  const [tids, setTids] = useState((preselected || []).join("\n"));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setTids((preselected || []).join("\n")); }, [preselected]);
  if (!open) return null;

  const submit = async () => {
    const tracking_ids = tids.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (!plate) return toast.error("Vehicle plate required");
    if (tracking_ids.length === 0) return toast.error("Pick at least 1 parcel");
    setSaving(true);
    try {
      const body = {
        vehicle: { plate, type: vtype, driver_name: driver, driver_phone: phone },
        courier_partner: courier,
        tracking_ids,
      };
      const { data } = await api.post("/hub/dispatch/manifests", body);
      toast.success(`Manifest ${data.manifest.manifest_id} created`);
      onCreated(data.manifest);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object") {
        toast.error(`Out-of-scope: ${(d.out_of_scope_or_missing || []).length} · already-on-manifest: ${(d.already_on_manifest || []).length}`);
      } else {
        toast.error(e.userMessage || "Create failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mhm-manifest-create-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Outfit'] text-lg font-semibold">Create manifest</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Plate</label>
            <input data-testid="mhm-manifest-plate" value={plate} onChange={(e) => setPlate(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Vehicle type</label>
            <select value={vtype} onChange={(e) => setVtype(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5">
              <option value="van">Van</option><option value="tempo">Tempo</option><option value="bike">Bike</option>
            </select>
          </div>
          <div><label className="block text-xs text-slate-500 mb-1">Driver name</label>
            <input value={driver} onChange={(e) => setDriver(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/></div>
          <div><label className="block text-xs text-slate-500 mb-1">Driver phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/></div>
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Courier partner</label>
            <select data-testid="mhm-manifest-courier" value={courier} onChange={(e) => setCourier(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5">
              <option value="auto">Auto-select (cheapest)</option>
              {(couriers || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Tracking IDs ({(tids.split(/[\s,]+/).filter(Boolean)).length})</label>
            <textarea data-testid="mhm-manifest-tids" value={tids} onChange={(e) => setTids(e.target.value)}
                      rows={4} className="w-full font-mono text-xs border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-slate-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving}
                  data-testid="mhm-manifest-submit"
                  className="text-sm px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-60">
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DispatchCenter() {
  const [queue, setQueue] = useState(null);
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [couriers, setCouriers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, m, c] = await Promise.all([
        api.get("/hub/dispatch/queue"),
        api.get("/hub/dispatch/manifests"),
        api.get("/hub/couriers/list"),
      ]);
      setQueue(q.data); setManifests(m.data.rows || []);
      setCouriers(c.data.couriers || []);
    } catch (e) {
      toast.error(e.userMessage || "Couldn't load dispatch");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const suggestBatches = async () => {
    try {
      const { data } = await api.post("/hub/dispatch/batch-suggest", { horizon_minutes: 240 });
      if ((data.batches || []).length === 0) {
        toast.info("No batches to suggest right now");
        return;
      }
      const set = new Set();
      data.batches[0].tracking_ids.forEach((t) => set.add(t));
      setSelected(set);
      toast.success(`Suggested ${data.batches[0].tracking_ids.length} parcels for ${data.batches[0].destination_city}`);
    } catch (e) { toast.error(e.userMessage || "Suggest failed"); }
  };

  const toggle = (tid) => {
    const next = new Set(selected);
    next.has(tid) ? next.delete(tid) : next.add(tid);
    setSelected(next);
  };

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-dispatch-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-dispatch-title">
              Dispatch Center
            </h1>
            <p className="text-sm text-slate-500">
              Queue: {queue?.count ?? 0} parcels in {queue?.groups?.length ?? 0} groups · {manifests.length} manifests
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SlaAlertBadge />
            <button onClick={load} data-testid="mhm-dispatch-refresh"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
            <button onClick={suggestBatches}
                    data-testid="mhm-dispatch-suggest"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-violet-200 hover:bg-violet-50 text-violet-700 rounded-lg">
              <Sparkles className="h-4 w-4" /> Suggest batches
            </button>
            <button onClick={() => setCreateOpen(true)}
                    disabled={selected.size === 0}
                    data-testid="mhm-dispatch-create-manifest"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg">
              <Plus className="h-4 w-4" /> Create manifest ({selected.size})
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEFT — Queue */}
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="mhm-dispatch-queue">
            <h3 className="font-semibold mb-2 text-slate-700">Dispatch queue · grouped by destination</h3>
            {(queue?.groups || []).length === 0 && (
              <div className="text-sm text-slate-400 py-8 text-center">No parcels eligible for dispatch</div>
            )}
            {(queue?.groups || []).map((g) => (
              <details key={`${g.destination_city}-${g.pin_prefix}`} className="border border-slate-100 rounded-lg mb-2">
                <summary className="cursor-pointer px-3 py-2 flex justify-between items-center text-sm hover:bg-slate-50">
                  <span><b>{g.destination_city}</b> · pin {g.pin_prefix} · {g.count} parcels · {g.total_weight_kg.toFixed(2)} kg</span>
                </summary>
                <ul className="text-xs px-3 py-2 max-h-40 overflow-y-auto space-y-1">
                  {g.tracking_ids.map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <input type="checkbox" checked={selected.has(t)} onChange={() => toggle(t)}
                             data-testid={`mhm-dispatch-tid-${t}`}/>
                      <TrackingIdLink tid={t} />
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>

          {/* RIGHT — Manifests */}
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="mhm-dispatch-manifests">
            <h3 className="font-semibold mb-2 text-slate-700">Active manifests · {manifests.length}</h3>
            {manifests.length === 0 && (
              <div className="text-sm text-slate-400 py-8 text-center">No manifests yet</div>
            )}
            <ul className="space-y-2">
              {manifests.map((m) => (
                <li key={m.manifest_id} className="border border-slate-100 rounded-lg p-3" data-testid={`mhm-manifest-row-${m.manifest_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Link to={`/dashboard/hub/dispatch/${m.manifest_id}`}
                            className="font-mono text-sm text-violet-700 hover:underline"
                            data-testid={`mhm-manifest-link-${m.manifest_id}`}>
                        {m.manifest_id}
                      </Link>
                      <div className="text-xs text-slate-500">
                        {m.vehicle?.plate || "—"} · {m.courier_partner} · {m.parcel_count} parcels · {m.total_weight_kg} kg
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      m.status === "dispatched_verified" ? "bg-green-100 text-green-800" :
                      m.status === "dispatched" ? "bg-blue-100 text-blue-800" :
                      "bg-slate-100 text-slate-700"
                    }`}>
                      <Truck className="inline h-3 w-3 mr-1" />{m.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <CreateManifestModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setSelected(new Set()); load(); }}
        preselected={[...selected]}
        couriers={couriers}
      />
    </RoleGate>
  );
}
